import React from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { EndBatchAction } from "./EndBatchSheet";

interface EndBatchConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: EndBatchAction;
  batchCode: string;
  abandonReason?: string;
  loading?: boolean;
  onConfirm: () => void;
}

export const EndBatchConfirmDialog: React.FC<EndBatchConfirmDialogProps> = ({
  open,
  onOpenChange,
  action,
  batchCode,
  abandonReason,
  loading,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const isAbandon = action === "abandon";

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("cbs.batch.confirmEnd", { defaultValue: "Are you sure?" })}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <span className="block">
              {isAbandon
                ? t("cbs.batch.confirmAbandon", {
                    defaultValue: `You are about to abandon batch "${batchCode}". This cannot be undone.`,
                    batchCode,
                  })
                : t("cbs.batch.confirmComplete", {
                    defaultValue: `You are about to complete batch "${batchCode}".`,
                    batchCode,
                  })}
            </span>
            {isAbandon && abandonReason && (
              <span className="block text-destructive font-medium">
                Reason: {abandonReason.replace(/_/g, " ")}
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={loading}
            className={isAbandon ? "bg-destructive hover:bg-destructive/90" : "bg-success hover:bg-success/90 text-success-foreground"}
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
          >
            {loading
              ? t("common.loading", { defaultValue: "Processing..." })
              : t("common.confirm", { defaultValue: "Confirm" })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
